/**
 * Underlying asset class: index, ETF or stock.
 *
 * Generated from major_tickers_all.json, the same file the backend mounts, so the frontend and the
 * server agree on what counts as an ETF. Only indices and ETFs are enumerated: everything else that
 * trades options is a stock, which is how the server-side filter is expressed too (an exclusion
 * rather than a 5,545-name list). A ticker added to the market tomorrow therefore classifies as a
 * stock on both sides instead of vanishing from a filtered board.
 */

export type AssetClass = 'index' | 'etf' | 'stock';

export const ASSET_CLASS_OPTIONS: { value: AssetClass; label: string }[] = [
  { value: 'stock', label: 'Stock' },
  { value: 'etf', label: 'ETF' },
  { value: 'index', label: 'Index' },
];

/** Index underlyings — cash-settled, and the ones Polygon's stocks snapshot does not price. */
export const INDEX_TICKERS: ReadonlySet<string> = new Set([
  'SPX', 'SPXW',
]);

export const ETF_TICKERS: ReadonlySet<string> = new Set([
  'AGG', 'ARKF', 'ARKG', 'ARKK', 'ARKQ', 'ARKW', 'BND', 'DIA', 'DRAM', 'EEM', 'EFA', 'EMB', 'FAS',
  'FAZ', 'HYG', 'IEF', 'IWM', 'LQD', 'PDBC', 'QQQ', 'SDOW', 'SHY', 'SMDD', 'SPXU', 'SPY', 'SQQQ',
  'TBT', 'TIP', 'TLT', 'TMF', 'TMV', 'TNA', 'TQQQ', 'TZA', 'UDOW', 'UMDD', 'UPRO', 'VDC', 'VDE',
  'VEA', 'VFH', 'VGT', 'VHT', 'VIG', 'VOO', 'VT', 'VTI', 'VTV', 'VUG', 'VWO', 'VXUS', 'VYM',
  'XLB', 'XLE', 'XLF', 'XLI', 'XLK', 'XLP', 'XLRE', 'XLU', 'XLV', 'XLY',
]);

/** Root symbol of an OCC-style symbol, so tape rows classify as readily as tiles. */
function root(ticker: string): string {
  return String(ticker || '').trim().toUpperCase().split(/\s+/)[0];
}

export function classifyTicker(ticker: string): AssetClass {
  const t = root(ticker);
  if (!t) return 'stock';
  if (INDEX_TICKERS.has(t)) return 'index';
  if (ETF_TICKERS.has(t)) return 'etf';
  return 'stock';
}

/** True when the ticker belongs to any of the selected classes (empty selection = no filter). */
export function matchesAssetClasses(ticker: string, selected: ReadonlySet<AssetClass>): boolean {
  if (!selected.size) return true;
  return selected.has(classifyTicker(ticker));
}
